import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  ClientAuthErrorCodes,
  EventType,
  InteractionRequiredAuthErrorCodes,
} from "@azure/msal-browser";
import type {
  AccountInfo,
  IPublicClientApplication,
  RedirectRequest,
} from "@azure/msal-browser";
import { describeAuthError, reportAuthEvent } from "./auth-diagnostics";

export type AuthFailureKind =
  | "transient"
  | "interaction-required"
  | "interaction-in-progress"
  | "fatal";

export type SignInReason =
  | "interaction-required"
  | "redirect-loop"
  | "redirect-failed"
  | "redirect-did-not-navigate"
  | "stale-interaction"
  | "unauthorized"
  | "unexpected-error";

export type SessionState =
  | { status: "ok" }
  | {
      status: "signin-required";
      reason: SignInReason;
      errorCode?: string;
      correlationId?: string;
    };

export type TokenRequestOptions = {
  forceRefresh?: boolean;
  claims?: string;
  /** Never redirects; failures surface through the session state instead. */
  background?: boolean;
};

type MsalClient = Pick<
  IPublicClientApplication,
  | "acquireTokenSilent"
  | "acquireTokenRedirect"
  | "loginRedirect"
  | "getActiveAccount"
  | "setActiveAccount"
  | "getAllAccounts"
  | "addEventCallback"
  | "removeEventCallback"
>;

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export type TokenBrokerOptions = {
  loginRequest: RedirectRequest;
  silentRedirectUri?: string;
  maxRetries?: number;
  retryBaseDelayMs?: number;
  maxRedirects?: number;
  redirectWindowMs?: number;
  sleep?: (ms: number) => Promise<void>;
  waitForOnline?: () => Promise<void>;
  now?: () => number;
  storage?: StorageLike | null;
};

const INTERACTION_REQUIRED_CODES = new Set<string>([
  InteractionRequiredAuthErrorCodes.interactionRequired,
  InteractionRequiredAuthErrorCodes.loginRequired,
  InteractionRequiredAuthErrorCodes.consentRequired,
  InteractionRequiredAuthErrorCodes.noTokensFound,
  InteractionRequiredAuthErrorCodes.refreshTokenExpired,
  InteractionRequiredAuthErrorCodes.badToken,
  InteractionRequiredAuthErrorCodes.uxNotAllowed,
  BrowserAuthErrorCodes.noAccountError,
  "invalid_grant",
]);

const TRANSIENT_CODES = new Set<string>([
  BrowserAuthErrorCodes.timedOut,
  BrowserAuthErrorCodes.monitorWindowTimeout,
  BrowserAuthErrorCodes.monitorPopupTimeout,
  BrowserAuthErrorCodes.noNetworkConnectivity,
  BrowserAuthErrorCodes.postRequestFailed,
  BrowserAuthErrorCodes.getRequestFailed,
  BrowserAuthErrorCodes.iframeClosedPrematurely,
  ClientAuthErrorCodes.networkError,
  ClientAuthErrorCodes.endpointResolutionError,
  "temporarily_unavailable",
  "server_error",
]);

const REDIRECT_LOG_KEY = "connect.auth.redirects";
const INTERACTION_STATUS_KEY = "msal.interaction.status";
const ONLINE_WAIT_MS = 10_000;

// Matches by error code instead of instanceof: apps may load a different MSAL copy than the shared package.
export function classifyAuthError(error: unknown): AuthFailureKind {
  const candidate = (error ?? {}) as { errorCode?: unknown; name?: unknown };
  const code =
    typeof candidate.errorCode === "string" ? candidate.errorCode : "";

  if (code === BrowserAuthErrorCodes.interactionInProgress)
    return "interaction-in-progress";
  if (
    INTERACTION_REQUIRED_CODES.has(code) ||
    candidate.name === "InteractionRequiredAuthError"
  )
    return "interaction-required";
  if (TRANSIENT_CODES.has(code)) return "transient";
  return "fatal";
}

export function resolveAccount(
  client: Pick<
    IPublicClientApplication,
    "getActiveAccount" | "setActiveAccount" | "getAllAccounts"
  >
): AccountInfo | null {
  const active = client.getActiveAccount();
  if (active) return active;

  const [first] = client.getAllAccounts();
  if (!first) return null;

  client.setActiveAccount(first);
  return first;
}

function noAccountError() {
  return new BrowserAuthError(BrowserAuthErrorCodes.noAccountError, "");
}

function isOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function defaultSessionStorage(): StorageLike | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

function defaultWaitForOnline() {
  return new Promise<void>((resolve) => {
    const done = () => {
      clearTimeout(timer);
      window.removeEventListener("online", done);
      resolve();
    };
    const timer = setTimeout(done, ONLINE_WAIT_MS);
    window.addEventListener("online", done);
  });
}

export class TokenBroker {
  private readonly loginRequest: RedirectRequest;
  private readonly silentRedirectUri: string | undefined;
  private readonly maxRetries: number;
  private readonly retryBaseDelayMs: number;
  private readonly maxRedirects: number;
  private readonly redirectWindowMs: number;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly waitForOnline: () => Promise<void>;
  private readonly now: () => number;
  private readonly storage: StorageLike | null;
  private readonly inflight = new Map<string, Promise<string>>();
  private readonly listeners = new Set<() => void>();
  private state: SessionState = { status: "ok" };
  // While set, foreground requests fail fast instead of repeating a call that needs user interaction.
  private blocked = false;
  private lastError: unknown;
  // Kept so manual recovery can still satisfy a claims challenge that needed interaction.
  private pendingClaims: string | undefined;
  constructor(
    private readonly client: MsalClient,
    options: TokenBrokerOptions
  ) {
    this.loginRequest = options.loginRequest;
    this.silentRedirectUri = options.silentRedirectUri;
    this.maxRetries = options.maxRetries ?? 2;
    this.retryBaseDelayMs = options.retryBaseDelayMs ?? 500;
    this.maxRedirects = options.maxRedirects ?? 2;
    this.redirectWindowMs = options.redirectWindowMs ?? 60_000;
    this.sleep =
      options.sleep ??
      ((ms) => new Promise((resolve) => setTimeout(resolve, ms)));
    this.waitForOnline = options.waitForOnline ?? defaultWaitForOnline;
    this.now = options.now ?? Date.now;
    this.storage =
      options.storage === undefined ? defaultSessionStorage() : options.storage;

    client.addEventCallback(
      () => this.clearBlock(),
      [EventType.LOGIN_SUCCESS, EventType.ACTIVE_ACCOUNT_CHANGED]
    );
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getState = () => this.state;

  getToken(options: TokenRequestOptions = {}): Promise<string> {
    if (this.blocked && !options.background)
      return Promise.reject(this.lastError);

    const key = `${options.background ? 1 : 0}|${options.forceRefresh ? 1 : 0}|${options.claims ?? ""}`;
    const existing = this.inflight.get(key);
    if (existing) return existing;

    const pending = this.acquire(options).finally(() => {
      this.inflight.delete(key);
    });
    this.inflight.set(key, pending);
    return pending;
  }

  async refresh(): Promise<void> {
    if (!resolveAccount(this.client)) return;
    try {
      await this.getToken({ background: true });
    } catch {
      // Already reported and reflected in the session state.
    }
  }

  async signIn(): Promise<void> {
    await this.redirect(null, noAccountError());
  }

  async recover(): Promise<void> {
    this.clearStaleInteraction();
    this.storage?.removeItem(REDIRECT_LOG_KEY);
    this.blocked = false;
    reportAuthEvent({ type: "recover", ...describeAuthError(this.lastError) });
    await this.redirect(
      resolveAccount(this.client),
      this.lastError ?? noAccountError(),
      this.pendingClaims
    );
  }

  /** The API rejected a token that MSAL considered valid, even after a forced refresh. */
  reportRejectedSession(error: unknown) {
    this.fail("unauthorized", error, false);
  }

  private async acquire(options: TokenRequestOptions): Promise<string> {
    for (let attempt = 0; ; attempt++) {
      const account = resolveAccount(this.client);
      if (!account)
        return this.requireInteraction(null, noAccountError(), options);

      try {
        const result = await this.client.acquireTokenSilent({
          ...this.loginRequest,
          ...(this.silentRedirectUri
            ? { redirectUri: this.silentRedirectUri }
            : {}),
          account,
          forceRefresh: options.forceRefresh,
          claims: options.claims,
        });
        this.markSignedIn();
        if (options.claims) this.pendingClaims = undefined;
        return result.accessToken;
      } catch (error) {
        const kind = classifyAuthError(error);
        reportAuthEvent({
          type: "token-failure",
          decision: kind,
          attempt,
          ...describeAuthError(error),
        });

        const retryable =
          kind === "transient" || kind === "interaction-in-progress";
        if (retryable && attempt < this.maxRetries) {
          await this.pause(attempt);
          continue;
        }
        if (kind === "interaction-required")
          return this.requireInteraction(account, error, options);
        if (
          typeof error === "object" &&
          error !== null &&
          "errorCode" in error &&
          error.errorCode === BrowserAuthErrorCodes.monitorWindowTimeout
        ) {
          if (options.claims) this.pendingClaims = options.claims;
          this.fail("interaction-required", error, true);
        }
        if (kind === "interaction-in-progress")
          this.fail("stale-interaction", error, false);
        else if (kind === "fatal") this.fail("unexpected-error", error, false);
        throw error;
      }
    }
  }

  private async requireInteraction(
    account: AccountInfo | null,
    error: unknown,
    options: TokenRequestOptions
  ): Promise<never> {
    if (options.background) this.fail("interaction-required", error, true);
    else {
      if (options.claims) this.pendingClaims = options.claims;
      await this.redirect(account, error, options.claims);
    }
    throw error;
  }

  private async redirect(
    account: AccountInfo | null,
    cause: unknown,
    claims?: string
  ) {
    if (!this.reserveRedirect()) {
      this.fail("redirect-loop", cause, true);
      return;
    }

    reportAuthEvent({
      type: "redirect",
      decision: account ? "acquireTokenRedirect" : "loginRedirect",
      ...describeAuthError(cause),
    });

    const request = claims
      ? { ...this.loginRequest, claims }
      : this.loginRequest;

    try {
      if (account)
        await this.client.acquireTokenRedirect({ ...request, account });
      else await this.client.loginRedirect(request);
    } catch (error) {
      const stale = classifyAuthError(error) === "interaction-in-progress";
      this.fail(stale ? "stale-interaction" : "redirect-failed", error, true);
      return;
    }

    // MSAL resolves only when the browser did not navigate away within its redirect timeout.
    this.fail("redirect-did-not-navigate", cause, true);
  }

  private async pause(attempt: number) {
    if (isOffline()) return this.waitForOnline();
    const delay = this.retryBaseDelayMs * 2 ** attempt;
    return this.sleep(delay + Math.random() * delay * 0.25);
  }

  private reserveRedirect() {
    if (!this.storage) return true;

    try {
      const cutoff = this.now() - this.redirectWindowMs;
      const parsed: unknown = JSON.parse(
        this.storage.getItem(REDIRECT_LOG_KEY) ?? "[]"
      );
      const recent = (Array.isArray(parsed) ? parsed : []).filter(
        (time): time is number => typeof time === "number" && time > cutoff
      );
      if (recent.length >= this.maxRedirects) return false;

      this.storage.setItem(
        REDIRECT_LOG_KEY,
        JSON.stringify([...recent, this.now()])
      );
    } catch {
      // Without usable storage the loop guard is unavailable; sign-in must still work.
    }
    return true;
  }

  private clearStaleInteraction() {
    for (const read of [defaultSessionStorage, () => globalThis.localStorage]) {
      try {
        read()?.removeItem(INTERACTION_STATUS_KEY);
      } catch {
        // Storage unavailable.
      }
    }
  }

  private markSignedIn() {
    this.storage?.removeItem(REDIRECT_LOG_KEY);
    this.clearBlock();
  }

  private clearBlock() {
    this.blocked = false;
    this.lastError = undefined;
    this.setState({ status: "ok" });
  }

  private fail(reason: SignInReason, error: unknown, blocking: boolean) {
    const { errorCode, correlationId, ...rest } = describeAuthError(error);
    this.blocked = blocking;
    this.lastError = error;
    reportAuthEvent({
      type: "signin-required",
      decision: reason,
      errorCode,
      correlationId,
      ...rest,
    });
    this.setState({
      status: "signin-required",
      reason,
      errorCode,
      correlationId,
    });
  }

  private setState(next: SessionState) {
    const current = this.state;
    const unchanged =
      current.status === next.status &&
      (next.status === "ok" ||
        (current.status === "signin-required" &&
          current.reason === next.reason &&
          current.errorCode === next.errorCode &&
          current.correlationId === next.correlationId));
    if (unchanged) return;

    this.state = next;
    this.listeners.forEach((listener) => listener());
  }
}

/** Refreshes the session when the tab regains focus or connectivity so later requests find a warm token. */
export function startSessionRefreshTriggers(
  broker: Pick<TokenBroker, "refresh">,
  { minIntervalMs = 30_000, now = Date.now } = {}
): () => void {
  if (typeof window === "undefined") return () => {};

  let last = Number.NEGATIVE_INFINITY;
  const run = () => {
    if (document.visibilityState === "hidden" || now() - last < minIntervalMs)
      return;
    last = now();
    void broker.refresh();
  };

  window.addEventListener("online", run);
  window.addEventListener("focus", run);
  document.addEventListener("visibilitychange", run);
  return () => {
    window.removeEventListener("online", run);
    window.removeEventListener("focus", run);
    document.removeEventListener("visibilitychange", run);
  };
}
